import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
    CalendarDays,
    ClipboardList,
    FolderKanban,
    Gauge,
    LifeBuoy,
    PieChart,
    ShieldCheck,
    Users2,
    ScrollText,
    Video,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const ADMIN_ROLES = ['Manager', 'Head of Department', 'Developer'];

/**
 * Navigation is grouped so the menu reads as an information architecture rather
 * than a flat list. Routes are unchanged — only presentation and grouping.
 */
const buildNavigation = (isGlobalAdmin) => [
    {
        label: 'Overview',
        items: [
            { name: 'Dashboard', path: '/dashboard', icon: Gauge, show: isGlobalAdmin },
            { name: 'Calendar', path: '/calendar', icon: CalendarDays, show: true },
        ],
    },
    {
        label: 'Delivery',
        items: [
            { name: 'Projects', path: '/projects', icon: FolderKanban, show: true },
            { name: 'Tasks', path: '/tasks', icon: ClipboardList, show: true },
            { name: 'Tickets', path: '/tickets', icon: LifeBuoy, show: true },
        ],
    },
    {
        label: 'Governance',
        items: [
            { name: 'Meetings', path: '/meetings', icon: Video, show: true },
            { name: 'Excom', path: '/excom', icon: ShieldCheck, show: true },
            { name: 'Governance Hub', path: '/governance', icon: ScrollText, show: true },
            { name: 'Reports', path: '/reports', icon: PieChart, show: isGlobalAdmin },
            { name: 'Users', path: '/users', icon: Users2, show: isGlobalAdmin },
        ],
    },
];

const Sidebar = ({ collapsed, mobileOpen, onCloseMobile }) => {
    const { user } = useAuth();
    const isGlobalAdmin = user && ADMIN_ROLES.includes(user.role);

    const groups = buildNavigation(isGlobalAdmin)
        .map((g) => ({ ...g, items: g.items.filter((i) => i.show) }))
        .filter((g) => g.items.length > 0);

    return (
        <>
            {/* Mobile scrim */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 z-overlay lg:hidden animate-fade-in"
                    style={{ background: 'var(--bg-overlay)' }}
                    onClick={onCloseMobile}
                    aria-hidden="true"
                />
            )}

            <aside
                className={`app-sidebar fixed top-0 left-0 z-sidebar h-screen flex flex-col
                    transition-[width,transform] duration-200 ease-out
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
                style={{
                    width: collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)',
                    backgroundColor: 'var(--bg-sidebar)',
                    zIndex: mobileOpen ? 85 : undefined,
                }}
                aria-label="Main navigation"
            >
                {/* Brand */}
                <div
                    className="flex items-center shrink-0 px-4"
                    style={{
                        height: 'var(--topbar-h)',
                        borderBottom: '1px solid var(--border-sidebar)',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                    }}
                >
                    <Link to="/" className="flex items-center gap-2.5 min-w-0" onClick={onCloseMobile}>
                        <img
                            src="/logo blue.png"
                            alt=""
                            aria-hidden="true"
                            className="h-12 w-auto object-contain brightness-0 invert shrink-0"
                        />
                        {/* {!collapsed && (
                            <span className="text-white font-semibold text-md tracking-tight truncate">
                                MyBank <span className="font-normal opacity-70">ICT</span>
                            </span>
                        )} */}
                    </Link>
                </div>

                {/* Menu */}
                <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 scrollbar-hide">
                    {groups.map((group, gi) => (
                        <div key={group.label}>
                            {collapsed ? (
                                gi > 0 && (
                                    <div
                                        className="my-2 mx-3 h-px"
                                        style={{ backgroundColor: 'var(--border-sidebar)' }}
                                        aria-hidden="true"
                                    />
                                )
                            ) : (
                                <p className="nav-group-label" style={gi === 0 ? { marginTop: 4 } : undefined}>
                                    {group.label}
                                </p>
                            )}

                            <ul className="space-y-0.5">
                                {group.items.map((item) => (
                                    <li key={item.path}>
                                        <NavLink
                                            to={item.path}
                                            onClick={onCloseMobile}
                                            title={collapsed ? item.name : undefined}
                                            className={({ isActive }) =>
                                                `nav-link ${isActive ? 'nav-link-active' : ''} ${collapsed ? 'justify-center px-0' : ''
                                                }`
                                            }
                                        >
                                            <item.icon size={18} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
                                            {!collapsed && <span className="truncate">{item.name}</span>}
                                            {collapsed && <span className="sr-only">{item.name}</span>}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>

                {/* Signed-in identity */}
                {!collapsed && user && (
                    <div
                        className="shrink-0 px-4 py-3"
                        style={{ borderTop: '1px solid var(--border-sidebar)' }}
                    >
                        <p className="text-label font-medium text-white truncate">
                            {user.first_name} {user.last_name}
                        </p>
                        <p className="text-micro truncate" style={{ color: 'var(--text-sidebar)' }}>
                            {user.role}
                        </p>
                    </div>
                )}
            </aside>
        </>
    );
};

export default Sidebar;
